import { useEffect, useState } from "react";
import { ExternalLink, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DeadlineBadge } from "./DeadlineBadge";
import {
  SOURCE_LABEL,
  STATUS_LABEL,
  STATUS_ORDER,
} from "./opportunityConstants";
import type {
  Opportunity,
  OpportunitySource,
  OpportunityStatus,
  OpportunityUpdate,
} from "./opportunityTypes";

const SOURCES: OpportunitySource[] = [
  "SECOP",
  "Convocatoria",
  "InnovationCall",
  "DirectAward",
  "PartnerLead",
  "Manual",
];

interface OpportunityDialogProps {
  opportunity: Opportunity | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (id: string, patch: OpportunityUpdate) => Promise<void> | void;
  onDelete?: (id: string) => Promise<void> | void;
  canDelete?: boolean;
}

type FormState = {
  title: string;
  entity: string;
  status: OpportunityStatus;
  source: OpportunitySource;
  url: string;
  deadline: string;
  budget_cop: string;
  fit_score: string;
  needs_partner: boolean;
  eligibility_notes: string;
  notes: string;
};

function toForm(o: Opportunity): FormState {
  return {
    title: o.title ?? "",
    entity: o.entity ?? "",
    status: o.status,
    source: o.source,
    url: o.url ?? "",
    deadline: o.deadline ?? "",
    budget_cop: o.budget_cop != null ? String(o.budget_cop) : "",
    fit_score: o.fit_score != null ? String(o.fit_score) : "",
    needs_partner: o.needs_partner,
    eligibility_notes: o.eligibility_notes ?? "",
    notes: o.notes ?? "",
  };
}

export function OpportunityDialog({
  opportunity,
  open,
  onOpenChange,
  onSave,
  onDelete,
  canDelete = false,
}: OpportunityDialogProps) {
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(opportunity ? toForm(opportunity) : null);
  }, [opportunity]);

  if (!opportunity || !form) return null;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(opportunity.id, {
        title: form.title.trim(),
        entity: form.entity.trim() || null,
        status: form.status,
        source: form.source,
        url: form.url.trim() || null,
        deadline: form.deadline || null,
        budget_cop: form.budget_cop ? Number(form.budget_cop) : null,
        fit_score: form.fit_score ? Number(form.fit_score) : null,
        needs_partner: form.needs_partner,
        eligibility_notes: form.eligibility_notes.trim() || null,
        notes: form.notes.trim() || null,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="pr-6 text-base leading-snug">{opportunity.title}</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2 pt-1">
            <span>{SOURCE_LABEL[opportunity.source]}</span>
            {opportunity.department && <span>· {opportunity.department}</span>}
            <DeadlineBadge deadline={opportunity.deadline} />
          </DialogDescription>
        </DialogHeader>

        {opportunity.description && (
          <p className="rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
            {opportunity.description}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="opp-title">Title</Label>
            <Input
              id="opp-title"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="opp-entity">Entity</Label>
            <Input
              id="opp-entity"
              value={form.entity}
              onChange={(e) => set("entity", e.target.value)}
              placeholder="Corporación Ruta N Medellín"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="opp-deadline">Deadline</Label>
            <Input
              id="opp-deadline"
              type="date"
              value={form.deadline}
              onChange={(e) => set("deadline", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={form.status}
              onValueChange={(v) => set("status", v as OpportunityStatus)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_ORDER.map((s) => (
                  <SelectItem key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Source</Label>
            <Select
              value={form.source}
              onValueChange={(v) => set("source", v as OpportunitySource)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {SOURCE_LABEL[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="opp-budget">Budget (COP)</Label>
            <Input
              id="opp-budget"
              type="number"
              inputMode="numeric"
              value={form.budget_cop}
              onChange={(e) => set("budget_cop", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="opp-fit">Fit score (1–5)</Label>
            <Input
              id="opp-fit"
              type="number"
              min={1}
              max={5}
              value={form.fit_score}
              onChange={(e) => set("fit_score", e.target.value)}
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="opp-url">Source URL</Label>
            <div className="flex gap-2">
              <Input
                id="opp-url"
                value={form.url}
                onChange={(e) => set("url", e.target.value)}
                placeholder="https://community.secop.gov.co/..."
              />
              {form.url && (
                <Button variant="outline" size="icon" asChild>
                  <a href={form.url} target="_blank" rel="noopener noreferrer" aria-label="Open">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 sm:col-span-2">
            <Switch
              id="opp-partner"
              checked={form.needs_partner}
              onCheckedChange={(v) => set("needs_partner", v)}
            />
            <Label htmlFor="opp-partner" className="cursor-pointer font-normal">
              Needs a prime partner or unión temporal
            </Label>
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="opp-elig">Eligibility — can we bid, and why not?</Label>
            <Textarea
              id="opp-elig"
              rows={4}
              value={form.eligibility_notes}
              onChange={(e) => set("eligibility_notes", e.target.value)}
              placeholder="Experiencia habilitante required? RUP? Financial ratios? Record what blocks us — that is what tells us which credential to go get."
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="opp-notes">Notes</Label>
            <Textarea
              id="opp-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {canDelete && onDelete ? (
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => onDelete(opportunity.id)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !form.title.trim()}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
